import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setUserKanbanLaneOrder, getUserKanbanLaneOrder } from '../../services/userKanbanService';
import { Button, TextField, Paper, FormControl, Select, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, List, ListItem, ListItemText, ListItemAvatar, Avatar } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import PersonIcon from '@material-ui/icons/Person';
import { format } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import KanbanBoard from './KanbanBoard';

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    display: 'flex',
    padding: 0,
    margin: 0,
    marginTop: -16,
    overflowX: 'auto',
    ...theme.scrollbarStyles,
    borderRadius: '0px',
    backgroundColor: '#f8f9fa',
    border: 'none',
    boxShadow: 'none',
    height: 'calc(100% + 16px)',
  },
  headerButtonsWrapper: {
    position: 'fixed',
    top: 16,
    right: 16,
    display: 'flex',
    gap: theme.spacing(1),
    zIndex: 1000,
  },
  iconButton: {
    backgroundColor: '#212529',
    color: '#fff',
    padding: theme.spacing(1.5),
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#495057',
    },
  },
  userSelectorDialog: {
    borderRadius: '12px',
    minWidth: '400px',
  },
  userListItem: {
    borderRadius: '8px',
    marginBottom: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
  },
  selectedUserItem: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '& .MuiListItemText-primary': {
      color: '#fff',
      fontWeight: 600,
    },
    '& .MuiListItemText-secondary': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  userAvatar: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  dialogPaper: {
    borderRadius: '12px',
    minWidth: '500px',
  },
  filterSection: {
    marginBottom: theme.spacing(2),
  },
  filterLabel: {
    fontSize: '0.9rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: '#212529',
  },
  filterControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
  },
  dateInput: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
    marginBottom: theme.spacing(1),
  },
  dialogButton: {
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userSelectorOpen, setUserSelectorOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(user.id);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Carregar ordem das lanes do banco de dados (individual por usuário)
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        // Se for admin, carregar a ordem do usuário selecionado
        const userIdToLoad = user.profile === 'admin' && selectedUserId ? selectedUserId : user.id;
        const savedOrder = await getUserKanbanLaneOrder(userIdToLoad);
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };

    if (user && user.id) {
      loadLaneOrder();
    }
  }, [user, selectedUserId]);

  useEffect(() => {
    fetchTags();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarregar tickets quando o usuário selecionado mudar (admin)
  useEffect(() => {
    if (tags.length > 0) {
      fetchTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users/');
      setUsers(data.users || []);
    } catch (error) {
      console.log('Erro ao carregar usuários:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async (fetchedTags = tags) => {
    try {
      // Buscar tickets:
      // - Tickets pendentes (sem userId) aparecem para todos
      // - Tickets com userId aparecem apenas para aquele usuário (ou admin visualizando)
      const params = {
        queueIds: JSON.stringify(queueIds),
        startDate: startDate,
        endDate: endDate,
      };

      // Se for admin visualizando um usuário específico, filtrar por ele
      if (user.profile === 'admin' && selectedUserId) {
        params.users = JSON.stringify([parseInt(selectedUserId, 10)]);
      }

      const { data } = await api.get('/ticket/kanban', { params });
      setTickets(data.tickets);
      organizeLanes(fetchedTags, data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (['create', 'update', 'delete'].includes(data.action)) {
        fetchTickets();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = updatedTicket => {
    setTickets(prevTickets =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
  };

  const getOpportunityValue = (ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return opportunityValue;
  };

  const organizeLanes = (fetchedTags = tags, fetchedTickets = tickets) => {
    let sortedTickets = [...fetchedTickets];

    // Aplicar filtros
    if (filterStatus === 'accepted') {
      sortedTickets = sortedTickets.filter(ticket => {
        const customFields = ticket.contact.extraInfo || [];
        const acceptedField = customFields.find(field => field.name === 'aceito');
        return acceptedField && acceptedField.value === 'true';
      });
    } else if (filterStatus === 'pending') {
      sortedTickets = sortedTickets.filter(ticket => {
        const customFields = ticket.contact.extraInfo || [];
        const acceptedField = customFields.find(field => field.name === 'aceito');
        return !acceptedField || acceptedField.value !== 'true';
      });
    }

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }

    const defaultTickets = sortedTickets.filter(
      ticket => ticket.tags.length === 0
    );

    const lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...fetchedTags.map(tag => {
        const taggedTickets = sortedTickets.filter(ticket =>
          ticket.tags.some(t => t.id === tag.id)
        );
        return {
          id: tag.id.toString(),
          title: tag.name,
          tickets: taggedTickets,
          color: tag.color || '#757575',
        };
      }),
    ];

    // Aplicar ordem personalizada se existir
    if (laneOrder && laneOrder.length > 0) {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      
      // Adicionar lanes na ordem salva
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      
      // Adicionar lanes restantes (novas lanes que não estavam na ordem salva)
      laneMap.forEach(lane => orderedLanes.push(lane));
      
      setLanes(orderedLanes);
    } else {
      setLanes(lanesData);
    }
  };

  useEffect(() => {
    if (!loadingLaneOrder) {
      organizeLanes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, tickets, sortOrder, laneOrder, loadingLaneOrder, filterStatus]);

  const handleCardMove = async (ticketId, targetLaneId, sourceLaneId, sourceIndex, destinationIndex) => {
    ticketId = parseInt(ticketId, 10);
    try {
      // Remover tags antigas
      await api.delete(`/ticket-tags/${ticketId}`);

      // Se moveu para uma coluna que não seja a padrão (lane0)
      if (targetLaneId !== 'lane0') {
        // Adicionar nova tag
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
        
        // Atribuir ao usuário selecionado (para admin) ou ao próprio usuário (para não-admin)
        const userIdToAssign = selectedUserId;
        
        // Atribuir o ticket ao usuário
        await api.put(`/tickets/${ticketId}`, {
          userId: parseInt(userIdToAssign, 10),
          status: 'open'
        });
        
        toast.success('Ticket atribuído com sucesso!');
      } else {
        // Devolvendo para a fila (lane0)
        // Remover atribuição do usuário e mudar status para pending
        await api.put(`/tickets/${ticketId}`, {
          userId: null,
          status: 'pending'
        });
        
        toast.success('Ticket devolvido para a fila!');
      }

      fetchTickets();
    } catch (err) {
      console.log(err);
      toast.error('Erro ao mover ticket');
    }
  };

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleFilterStatusChange = event => {
    setFilterStatus(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    // Qualquer usuário pode reordenar suas próprias lanes
    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    
    setLanes(newLanes);
    
    // Salvar nova ordem no banco de dados (individual por usuário)
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    
    try {
      // Se for admin, salvar para o usuário selecionado
      const userIdToSave = user.profile === 'admin' && selectedUserId ? selectedUserId : user.id;
      await setUserKanbanLaneOrder(newLaneOrder, userIdToSave);
      toast.success('Ordem das lanes atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao salvar ordem das lanes:', error);
      toast.error('Erro ao salvar ordem das lanes');
    }
  };

  const handleSelectedUserChange = (userId) => {
    setSelectedUserId(userId);
    setUserSelectorOpen(false);
  };

  return (
    <MainContainer>
      {/* Botões fixos no canto superior direito */}
      <div className={classes.headerButtonsWrapper}>
        <Can
          role={user.profile}
          perform="dashboard:view"
          yes={() => (
            <IconButton
              onClick={() => setUserSelectorOpen(true)}
              className={classes.iconButton}
              title="Gerenciar kanban de usuário"
            >
              <PersonIcon />
            </IconButton>
          )}
        />
        <IconButton
          onClick={() => setDrawerOpen(true)}
          className={classes.iconButton}
          title="Filtros"
        >
          <FilterListIcon />
        </IconButton>
        <Can
          role={user.profile}
          perform="dashboard:view"
          yes={() => (
            <IconButton
              onClick={handleAddColumnClick}
              className={classes.iconButton}
              title="Adicionar colunas"
            >
              <span style={{ fontSize: '1.2rem' }}>+</span>
            </IconButton>
          )}
        />
      </div>

      {/* Dialog de Seleção de Usuário */}
      <Dialog
        open={userSelectorOpen}
        onClose={() => setUserSelectorOpen(false)}
        classes={{ paper: classes.userSelectorDialog }}
      >
        <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Gerenciar Kanban do Usuário
          <IconButton onClick={() => setUserSelectorOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 20, paddingBottom: 20 }}>
          <List>
            {users.map(u => (
              <ListItem
                key={u.id}
                button
                onClick={() => handleSelectedUserChange(u.id)}
                className={`${classes.userListItem} ${
                  selectedUserId === u.id ? classes.selectedUserItem : ''
                }`}
              >
                <ListItemAvatar>
                  <Avatar className={classes.userAvatar}>
                    {u.name.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary={u.name}
                  secondary={selectedUserId === u.id ? 'Gerenciando' : ''}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Dialog de Filtros */}
      <Dialog
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Filtros
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 20 }}>
          {/* Ordenar por */}
          <div className={classes.filterSection}>
            <div className={classes.filterLabel}>Ordenar por</div>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterControl}
            >
              <Select
                native
                value={sortOrder}
                onChange={handleSortOrderChange}
              >
                <option value="ticketNumber">Número do Ticket</option>
                <option value="lastMessageTime">Última Mensagem</option>
                <option value="valorDesc">Valor (maior para menor)</option>
              </Select>
            </FormControl>
          </div>

          {/* Status */}
          <div className={classes.filterSection}>
            <div className={classes.filterLabel}>Status</div>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterControl}
            >
              <Select
                native
                value={filterStatus}
                onChange={handleFilterStatusChange}
              >
                <option value="all">Todos</option>
                <option value="accepted">Aceitos</option>
                <option value="pending">Pendentes</option>
              </Select>
            </FormControl>
          </div>

          {/* Data de início */}
          <div className={classes.filterSection}>
            <div className={classes.filterLabel}>Data de início</div>
            <TextField
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              className={classes.dateInput}
              size="small"
            />
          </div>

          {/* Data de fim */}
          <div className={classes.filterSection}>
            <div className={classes.filterLabel}>Data de fim</div>
            <TextField
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              className={classes.dateInput}
              size="small"
            />
          </div>
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button
            onClick={() => setDrawerOpen(false)}
            color="secondary"
            variant="outlined"
            className={classes.dialogButton}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleSearchClick();
              setDrawerOpen(false);
            }}
            className={classes.dialogButton}
          >
            Buscar
          </Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" className={classes.mainPaper}>
        <KanbanBoard
          lanes={lanes}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          isAdmin={user.profile === 'admin'}
        />
      </Paper>
    </MainContainer>
  );
};

export default Kanban;
